import { useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { Button } from "@/components/ui/Button";
import { Card, CardBody } from "@/components/ui/Card";
import { SearchableMultiSelect } from "@/components/ui/SearchableMultiSelect";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { apiFetch } from "@/lib/api";
import {
  WirelessDeviceLink,
  WirelessDeviceSerialLookupLink,
  WirelessHubTabs,
  formatWirelessCell,
  groupIdsToParam,
} from "./wirelessUiShared";
import { WirelessReportDataPanel } from "./WirelessReportDataPanel";

const TABS = [
  { key: "groups", label: "Groups" },
  { key: "users", label: "Users" },
  { key: "devices", label: "Devices" },
  { key: "currents", label: "Hourly currents" },
  { key: "events", label: "Events" },
] as const;

type OrgTab = (typeof TABS)[number]["key"];

type GroupRow = Record<string, string | number | null>;
type UserRow = Record<string, string | number | null> & { role: string };
type OrgDeviceRow = {
  deviceId: number;
  realName: string;
  imei: string;
  hubSerial: string;
  groupNames: string;
};

const GROUP_COLUMNS: HubDataTableColumn<GroupRow>[] = [
  {
    id: "GroupID",
    header: "GroupID",
    overflow: "nowrap",
    sortValue: (row) => row.GroupID ?? "",
    cell: (row) => formatWirelessCell(row.GroupID),
  },
  {
    id: "GroupName",
    header: "GroupName",
    sortValue: (row) => row.GroupName ?? "",
    cell: (row) => formatWirelessCell(row.GroupName),
  },
  {
    id: "IsOrganizationDefault",
    header: "IsOrganizationDefault",
    overflow: "nowrap",
    sortValue: (row) => row.IsOrganizationDefault ?? "",
    cell: (row) => formatWirelessCell(row.IsOrganizationDefault),
  },
  {
    id: "ConfigProfile",
    header: "ConfigProfile",
    sortValue: (row) => row.ConfigProfile ?? "",
    cell: (row) => formatWirelessCell(row.ConfigProfile),
  },
];

const USER_COLUMNS: HubDataTableColumn<UserRow>[] = [
  {
    id: "FirstName",
    header: "FirstName",
    sortValue: (row) => row.FirstName ?? "",
    cell: (row) => formatWirelessCell(row.FirstName),
  },
  {
    id: "LastName",
    header: "LastName",
    sortValue: (row) => row.LastName ?? "",
    cell: (row) => formatWirelessCell(row.LastName),
  },
  {
    id: "ContactEmail",
    header: "ContactEmail",
    sortValue: (row) => row.ContactEmail ?? "",
    cell: (row) => formatWirelessCell(row.ContactEmail),
  },
  {
    id: "ContactPhone",
    header: "ContactPhone",
    overflow: "nowrap",
    sortValue: (row) => row.ContactPhone ?? "",
    cell: (row) => formatWirelessCell(row.ContactPhone),
  },
  {
    id: "role",
    header: "role",
    overflow: "nowrap",
    sortValue: (row) => row.role ?? "",
    cell: (row) => formatWirelessCell(row.role),
  },
];

const DEVICE_COLUMNS: HubDataTableColumn<OrgDeviceRow>[] = [
  {
    id: "hubSerial",
    header: "Device serial",
    overflow: "nowrap",
    sortValue: (row) => row.hubSerial,
    cell: (row) => <WirelessDeviceSerialLookupLink serial={row.hubSerial} />,
  },
  {
    id: "imei",
    header: "IMEI",
    overflow: "nowrap",
    sortValue: (row) => row.imei,
    cell: (row) => <WirelessDeviceLink deviceId={row.deviceId} label={row.imei || "—"} className="font-mono" />,
  },
  {
    id: "realName",
    header: "Name",
    sortValue: (row) => row.realName,
    cell: (row) => row.realName || "—",
  },
  {
    id: "groupNames",
    header: "Groups",
    sortValue: (row) => row.groupNames,
    cell: (row) => row.groupNames,
  },
];

export function WirelessOrganizationDetailPage() {
  const { organizationId: organizationIdParam } = useParams();
  const organizationId = Number(organizationIdParam);
  const [searchParams, setSearchParams] = useSearchParams();
  const downloadCsvRef = useRef<(() => Promise<void>) | null>(null);
  const [downloadDisabled, setDownloadDisabled] = useState(true);
  const [groupSearch, setGroupSearch] = useState("");
  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<string>>(() => {
    const raw = searchParams.get("groupIds");
    if (!raw) return new Set();
    return new Set(
      raw
        .split(",")
        .map((value) => value.trim())
        .filter((value) => value !== ""),
    );
  });

  const activeTab = useMemo((): OrgTab => {
    const raw = searchParams.get("tab");
    if (raw === "groups" || raw === "users" || raw === "devices" || raw === "currents" || raw === "events") return raw;
    return "groups";
  }, [searchParams]);

  const groupIdsParam = groupIdsToParam(selectedGroupIds);

  const detailQuery = useQuery({
    queryKey: ["wireless", "organization", organizationId],
    queryFn: () =>
      apiFetch<{ organization: Record<string, string | number | null> }>(
        `/api/wireless/organizations/${organizationId}`,
      ),
    enabled: Number.isFinite(organizationId) && organizationId > 0,
  });

  const groupOptionsQuery = useQuery({
    queryKey: ["wireless", "organization", organizationId, "group-options"],
    queryFn: () =>
      apiFetch<{ groups: Array<{ groupId: number; groupName: string }> }>(
        `/api/wireless/organizations/${organizationId}/group-options`,
      ),
    enabled: Number.isFinite(organizationId) && organizationId > 0,
  });

  const groupsQuery = useQuery({
    queryKey: ["wireless", "organization", organizationId, "groups", groupIdsParam],
    queryFn: () => {
      const params = groupIdsParam ? `?groupIds=${encodeURIComponent(groupIdsParam)}` : "";
      return apiFetch<{ groups: GroupRow[] }>(
        `/api/wireless/organizations/${organizationId}/groups${params}`,
      );
    },
    enabled: activeTab === "groups" && Number.isFinite(organizationId) && organizationId > 0,
  });

  const usersQuery = useQuery({
    queryKey: ["wireless", "organization", organizationId, "users", groupIdsParam],
    queryFn: () => {
      const params = groupIdsParam ? `?groupIds=${encodeURIComponent(groupIdsParam)}` : "";
      return apiFetch<{ users: UserRow[] }>(
        `/api/wireless/organizations/${organizationId}/users${params}`,
      );
    },
    enabled: activeTab === "users" && Number.isFinite(organizationId) && organizationId > 0,
  });

  const devicesQuery = useQuery({
    queryKey: ["wireless", "organization", organizationId, "devices", groupIdsParam],
    queryFn: () => {
      const params = new URLSearchParams({ page: "0", pageSize: "200" });
      if (groupIdsParam) params.set("groupIds", groupIdsParam);
      return apiFetch<{ devices: OrgDeviceRow[] }>(
        `/api/wireless/organizations/${organizationId}/devices?${params.toString()}`,
      );
    },
    enabled: activeTab === "devices" && Number.isFinite(organizationId) && organizationId > 0,
  });

  const organizationName =
    String(detailQuery.data?.organization?.Name ?? "").trim() || `Organization ${organizationId}`;

  const groupOptions = useMemo(
    () =>
      (groupOptionsQuery.data?.groups ?? []).map((group) => ({
        value: String(group.groupId),
        label: group.groupName || `Group ${group.groupId}`,
      })),
    [groupOptionsQuery.data?.groups],
  );

  function setTab(tab: string) {
    const next = new URLSearchParams(searchParams);
    next.set("tab", tab);
    if (groupIdsParam) next.set("groupIds", groupIdsParam);
    setSearchParams(next, { replace: true });
  }

  function applyGroupFilter(nextSelected: Set<string>) {
    setSelectedGroupIds(nextSelected);
    const next = new URLSearchParams(searchParams);
    const param = groupIdsToParam(nextSelected);
    if (param) next.set("groupIds", param);
    else next.delete("groupIds");
    setSearchParams(next, { replace: true });
  }

  if (!Number.isFinite(organizationId) || organizationId <= 0) {
    return <p className="text-sm text-red-700">Invalid organization.</p>;
  }

  const filtersActive = groupIdsParam !== "";
  const reportTab = activeTab === "currents" || activeTab === "events";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp hoverHint="Portal organization with groups, users, devices, and events. Group filter applies to every tab.">
          {organizationName}
        </PageTitleWithHelp>
        {reportTab ? (
          <Button
            type="button"
            disabled={downloadDisabled}
            onClick={() => {
              void downloadCsvRef.current?.();
            }}
          >
            Download CSV
          </Button>
        ) : null}
      </div>

      <Card>
        <CardBody className="py-3">
          <span className="block text-xs font-medium uppercase tracking-wide text-slate-600">Filter by group</span>
          <SearchableMultiSelect
            className="mt-1 max-w-xl"
            selectedValues={selectedGroupIds}
            onSelectedValuesChange={applyGroupFilter}
            options={groupOptions}
            searchValue={groupSearch}
            onSearchChange={setGroupSearch}
            placeholder="All groups"
            emptySelectionMeansAll
            isLoading={groupOptionsQuery.isLoading}
            loadingMessage="Loading groups..."
            emptyMessage="No groups match this search."
          />
        </CardBody>
      </Card>

      <WirelessHubTabs ariaLabel="Organization sections" tabs={[...TABS]} activeKey={activeTab} onChange={setTab} />

      {activeTab === "groups" ? (
        <HubDataTable
          title="Groups"
          columns={GROUP_COLUMNS}
          rows={groupsQuery.data?.groups ?? []}
          rowKey={(row) => String(row.GroupID ?? "")}
          loading={groupsQuery.isLoading}
          loadingMessage="Loading groups..."
          error={groupsQuery.isError ? "Could not load groups." : null}
          empty="No groups."
          isFiltered={filtersActive}
          filteredEmpty="No groups match this filter."
        />
      ) : null}

      {activeTab === "users" ? (
        <HubDataTable
          title="Users"
          columns={USER_COLUMNS}
          rows={usersQuery.data?.users ?? []}
          rowKey={(row) => String(row.UserID ?? `${row.ContactEmail}-${row.FirstName}`)}
          loading={usersQuery.isLoading}
          loadingMessage="Loading users..."
          error={usersQuery.isError ? "Could not load users." : null}
          empty="No users."
          isFiltered={filtersActive}
          filteredEmpty="No users match this filter."
        />
      ) : null}

      {activeTab === "devices" ? (
        <HubDataTable
          title="Devices"
          columns={DEVICE_COLUMNS}
          rows={devicesQuery.data?.devices ?? []}
          rowKey={(row) => row.deviceId}
          loading={devicesQuery.isLoading}
          loadingMessage="Loading devices..."
          error={devicesQuery.isError ? "Could not load devices." : null}
          empty="No devices."
          isFiltered={filtersActive}
          filteredEmpty="No devices match this filter."
        />
      ) : null}

      {activeTab === "currents" ? (
        <WirelessReportDataPanel
          kind="currents"
          organizationId={organizationId}
          groupIdsParam={groupIdsParam}
          downloadCsvRef={downloadCsvRef}
          onDownloadDisabledChange={setDownloadDisabled}
        />
      ) : null}

      {activeTab === "events" ? (
        <WirelessReportDataPanel
          kind="events"
          organizationId={organizationId}
          groupIdsParam={groupIdsParam}
          downloadCsvRef={downloadCsvRef}
          onDownloadDisabledChange={setDownloadDisabled}
        />
      ) : null}
    </div>
  );
}
